import { useCallback, useState, useEffect } from "react";
import { getSectionStylesURL, putGlobalSectionStylesURL } from "../../utils/ConstantsURL";
import { unwrapApiResponse } from "../../utils/apiResponse";

export const useGlobalSectionStyles = (token, documentId) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [sectionStyles, setSectionStyles] = useState(null);

  const loadSectionStyles = useCallback(async () => {
    if (!token || !documentId) return;

    setLoading(true);
    setError(null);
    try {
      const response = await fetch(getSectionStylesURL(documentId), {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Error ${response.status}: Al cargar estilos`);
      }

      const list = await unwrapApiResponse(response);
      setSectionStyles(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("Error loading section styles:", err);
      setError(err.message || "Error de conexión al cargar estilos");
    } finally {
      setLoading(false);
    }
  }, [token, documentId]);

  useEffect(() => {
    loadSectionStyles();
  }, [loadSectionStyles]);

  const updateGlobalStyles = useCallback(async (styles) => {
    if (!token || !documentId) {
      return { success: false, error: "Faltan credenciales o ID de documento" };
    }

    setSaving(true);
    setError(null);
    try {
      const response = await fetch(putGlobalSectionStylesURL(documentId), {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(styles),
      });

      const data = await unwrapApiResponse(response);

      if (data.updatedSections) {
        await loadSectionStyles(); 
      }

      return { success: true, data };
    } catch (err) {
      console.error("Error updating global styles:", err);
      const errorMessage = err.message || "Error al actualizar estilos globales";
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setSaving(false);
    }
  }, [token, documentId, loadSectionStyles]);

  return {
    sectionStyles: sectionStyles ?? [],
    loading,
    saving,
    error,
    refresh: loadSectionStyles,
    updateGlobalStyles
  };
};

export default useGlobalSectionStyles;
